import { batch, createEffect, createMemo, createSignal, onCleanup, type Accessor } from 'solid-js';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { can } from '../lib/permissions.generated';
import { elementAddress, wireOrderedSchematicElements } from '../lib/panelElements';
import { plainError } from '../lib/plainErrors';
import { elementLevels, levelFraction, type WireFrame, type WireMap } from '../lib/outputViewCodec';
import {
  createOutputViewKey, isNotMemberRefusal, OUTPUT_VIEW_HIDDEN_GRACE_MS, OutputViewPeer,
  type OutputViewAnswerVia, type OutputViewDuration, type OutputViewEndReason, type OutputViewGrant,
  type OutputViewMapState, type OutputViewPeerPhase, type OutputViewWireReason, type OutputViewWireState,
} from '../lib/outputViewPeer';
import { ROOM_LINK_LIVE_VIEW_PREFIX, roomLinkOffered, type RoomLinkLiveViewHandlers } from '../lib/roomLink';
import type { RoomLinkLiveViewDuration, RoomLinkLiveViewHolder } from '../lib/roomLinkCodec';
import { firestore, restFirestore } from '../lib/firebase';
import type { VenueContextValue } from './VenueProvider';

/** The broker's busy answer: who holds the room's live view session (legacy session path, D17). */
export interface OutputViewHolder {
  sessionId: string;
  state: 'AUTHORIZED' | 'ACTIVE';
  userUid: string;
  displayName: string;
  client: 'console' | 'wireview';
  requestedAt: number;
  expiresAt: number;
}

/**
 * Who is viewing, as the Effects screen names them (B77): from the broker's busy answer, or from the
 * controller's `liveViewBusy` on the room link, which names the person and since when but no client.
 */
export interface OutputViewHolderView {
  displayName: string;
  client?: 'console' | 'wireview';
  /** Since when, in epoch ms; absent when the answer carried no readable time. */
  sinceMs?: number;
  /** The holder's session, on the legacy path only: Take over ends it by id. */
  sessionId?: string;
}

function holderFromSession(holder: OutputViewHolder): OutputViewHolderView {
  return { displayName: holder.displayName, client: holder.client, sinceMs: holder.requestedAt * 1000, sessionId: holder.sessionId };
}

function holderFromLink(holder: RoomLinkLiveViewHolder): OutputViewHolderView {
  return { displayName: holder.displayName, sinceMs: holder.since };
}

type Phase = 'idle' | 'connecting' | 'active';

/** What the viewer's indicator shows: the running stream is indefinite, or ends at a local deadline. */
export type OutputViewRemaining = { kind: 'indefinite' } | { kind: 'countdown'; deadlineMs: number };

/**
 * The controller's end rule (its viewer.go `Tick`) on this browser's clock: a default stream ends 120 s
 * (livegate 12 s) after `fromMs` — the helloAck's arrival, or the moment a switch to default handed the
 * controller its grant — and never past the ticket's `hardExpiresAt`, which is epoch seconds on the
 * controller's clock (`clockOffsetUs` = controller minus browser). An extended stream has no deadline.
 */
export function outputViewDeadline(duration: OutputViewDuration, fromMs: number, hardExpiresAt: number,
  clockOffsetUs: bigint): number | undefined {
  if (duration === 'extended') return undefined;
  const limitMs = duration === 'livegate' ? 12_000 : 120_000;
  return Math.min(fromMs + limitMs, hardExpiresAt * 1000 - Number(clockOffsetUs) / 1000);
}

/**
 * The phase marks, in connection order: ms from sending the grant request (measured, never gated).
 * `offerCandidatesComplete` (this browser's gathering ended, every candidate trickled) can land before or after
 * `offerWritten`: the two run side by side.
 */
const PHASE_MARKS = [
  'grantReceived', 'offerWritten', 'offerCandidatesComplete', 'answerReceived', 'connected', 'channelOpen', 'helloAck',
  'firstMap', 'firstFrame',
] as const;
type PhaseMark = typeof PHASE_MARKS[number];
/** The one console line per stream that carries the phase timings; the live journey receipt reads it. */
const OUTPUT_VIEW_PHASES_PREFIX = '[output-view] phases ';

/** What the live view says about the wire: only what the Pi said, or that it has said nothing lately. */
export type OutputViewWire = (
  | { state: 'not_connected' }
  | { state: OutputViewMapState }
  | { state: OutputViewWireState; reason?: OutputViewWireReason }
) & { thinnedFrames?: string };

/**
 * The live view's one wire slot. The owner's words (2026-09-25, Q6 + Amendment 1); the owner edits this
 * table. A live wire shows nothing.
 */
export const OUTPUT_VIEW_WIRE_COPY = {
  notConnectedLabel: 'Not connected',
  noLinkLabel: 'Not connected: no link on the lighting port',
  noAddressLabel: 'Not connected: no address on the lighting port',
  sendFailedLabel: 'Output failing',
  blackoutLabel: 'Blackout',
  connectedNoMapLabel: 'Connected, waiting',
  mapFailedLabel: 'Preview failed',
  linkDeadLabel: 'The link to the controller went quiet.',
} as const;

export function outputViewWireLabel(wire: OutputViewWire): string {
  let label: string;
  switch (wire.state) {
    case 'live': label = ''; break;
    case 'blackout': label = OUTPUT_VIEW_WIRE_COPY.blackoutLabel; break;
    case 'connected-no-map': label = OUTPUT_VIEW_WIRE_COPY.connectedNoMapLabel; break;
    case 'map-failed': label = OUTPUT_VIEW_WIRE_COPY.mapFailedLabel; break;
    case 'held':
      if (wire.reason === 'no_link') label = OUTPUT_VIEW_WIRE_COPY.noLinkLabel;
      else if (wire.reason === 'no_address') label = OUTPUT_VIEW_WIRE_COPY.noAddressLabel;
      else if (wire.reason === 'send_failed') label = OUTPUT_VIEW_WIRE_COPY.sendFailedLabel;
      else label = OUTPUT_VIEW_WIRE_COPY.notConnectedLabel;
      break;
    default: label = OUTPUT_VIEW_WIRE_COPY.notConnectedLabel;
  }
  return wire.thinnedFrames && wire.thinnedFrames !== '0'
    ? label ? `${label} · ${wire.thinnedFrames}` : wire.thinnedFrames
    : label;
}

/** The parts of the room store the live view reads; the provider hands itself in. */
export type OutputViewVenue = Pick<VenueContextValue,
  'role' | 'deviceDoc' | 'selectedVenue' | 'selectedRoom' | 'user' | 'cloudReachable' | 'cloudCall' | 'roomLayout' | 'rig'
  | 'placementTargets' | 'roomLink' | 'roomLinkState'>;

export interface OutputViewStore {
  phase: Accessor<Phase>;
  active: Accessor<boolean>;
  visible: Accessor<boolean>;
  available: Accessor<boolean>;
  extended: Accessor<boolean>;
  setExtended: (value: boolean) => void;
  /**
   * The live picture by element address. Empty until the first frame paints; held through any stall, so
   * a running view never swaps to another picture; empty again when the session ends.
   */
  levels: Accessor<ReadonlyMap<string, number>>;
  unknownKeys: Accessor<ReadonlySet<string>>;
  holder: Accessor<OutputViewHolderView | undefined>;
  /**
   * What this viewer's own indicator shows, from the controller's helloAck until the stream ends; undefined
   * before then. Console-local: seeded by helloAck and the viewer's own duration requests, never by the
   * session document (CONTRACT-console B113, B114).
   */
  remaining: Accessor<OutputViewRemaining | undefined>;
  reason: Accessor<string>;
  /**
   * The Pi's wire state for the running stream; `not_connected` before it speaks or after 3 s with neither
   * a status nor an accepted frame.
   */
  wire: Accessor<OutputViewWire>;
  /** Whether the RUNNING stream is indefinite (its duration, not the next start's choice). */
  sessionExtended: Accessor<boolean>;
  /** The running stream's duration can be switched (an operator duration, not the test-only livegate). */
  sessionSwitchable: Accessor<boolean>;
  /** Switch the running stream between two minutes and indefinite without restarting it. */
  setSessionExtended: (value: boolean) => Promise<void>;
  start: () => Promise<void>;
  stop: () => void;
  takeOver: () => Promise<void>;
}

const EMPTY_LEVELS: ReadonlyMap<string, number> = new Map<string, number>();
/**
 * How far behind the controller's send clock a frame is shown. It absorbs relay jitter and one SCTP
 * retransmit at a ~100 ms round trip (prod TURN runs ~150 ms), so arrivals that bunch up still play out
 * evenly. A preview a sixth of a second late is invisible; a picture that stutters is not.
 */
const PLAYOUT_MS = 150;
const MAX_PLAYOUT_FRAMES = 16;
/**
 * Status arrives at 1 Hz and frames many times a second; 3 s with neither means the view no longer hears
 * the Pi. Frames count because the Pi's SCTP sends the unordered frame channel ahead of the ordered
 * control channel, so status can starve behind a heavy stream that is still arriving.
 */
const WIRE_SILENCE_MS = 3_000;
const NOT_CONNECTED: OutputViewWire = { state: 'not_connected' };
const CONTROLLER_END_REASONS = new Set<OutputViewEndReason>([
  'connect_timeout', 'device_revoked', 'lease_expired', 'membership_revoked',
  'operator_end', 'session_ceiling', 'link_dead', 'superseded', 'time_limit',
  'transport_closed', 'transport_policy',
]);

export function outputViewReason(reason: OutputViewEndReason, byName?: string): string {
  if (reason === 'taken_over') return byName ? `taken over by ${byName}` : '';
  const labels: Record<OutputViewEndReason, string> = {
    client_end: 'Stopped', connect_timeout: 'Connection timed out', device_revoked: 'Controller disconnected',
    lease_expired: 'Session expired', membership_revoked: 'Access ended',
    operator_end: 'Stopped by an operator',
    session_ceiling: 'Session ended', link_dead: OUTPUT_VIEW_WIRE_COPY.linkDeadLabel,
    superseded: 'Session replaced', taken_over: '', time_limit: 'Time limit reached',
    transport_closed: 'Connection ended', transport_policy: 'Connection ended',
  };
  return labels[reason] || 'Connection ended';
}

/**
 * The console's one live view. It is created once by `VenueProvider` (as `venue.outputView`), above the
 * screens, so moving between console tabs never ends a stream. A stream ends on Stop, the Pi's own end, a
 * venue/room change, sign-out or loss of availability, and after the browser tab is hidden for 30 s.
 *
 * Two carriers, one view (D14, D17). On a controller that publishes `controllerRoomLink` the view is frames
 * on the room's open link: Start is one `liveView` message, the controller's `liveViewAck` seeds the
 * countdown, and the controller holds the one-viewer lock (B119). On any other controller the view is its
 * own output-view session, as before; that path is kept only for the mixed-fleet window.
 */
export function createOutputViewSession(venue: OutputViewVenue): OutputViewStore {
  const [phase, setPhase] = createSignal<Phase>('idle');
  const [extended, setExtended] = createSignal(false);
  const [levels, setLevels] = createSignal<ReadonlyMap<string, number>>(EMPTY_LEVELS);
  const [unknownKeys, setUnknownKeys] = createSignal<ReadonlySet<string>>(new Set());
  const [holder, setHolder] = createSignal<OutputViewHolderView>();
  const [deadlineMs, setDeadlineMs] = createSignal<number>();
  const [reason, setReason] = createSignal('');
  const [reportedWire, setReportedWire] = createSignal<OutputViewWire>(NOT_CONNECTED);
  const [mapState, setMapState] = createSignal<OutputViewMapState | null>(null);
  const wire = createMemo<OutputViewWire>(() => mapState()
    ? { state: mapState()!, thinnedFrames: reportedWire().thinnedFrames } : reportedWire());
  const [sessionDuration, setSessionDuration] = createSignal<OutputViewDuration>('default');
  // While a switch is in flight the control shows what the person chose; if the broker refuses, it
  // returns to the running duration.
  const [pendingExtended, setPendingExtended] = createSignal<boolean>();
  const sessionExtended = createMemo(() => pendingExtended() ?? sessionDuration() === 'extended');
  const sessionSwitchable = createMemo(() => phase() === 'active' && sessionDuration() !== 'livegate');
  /** The room's controller carries the view on its link (S3); otherwise the legacy session path. */
  const onLink = createMemo(() => roomLinkOffered(venue.deviceDoc()));
  const visible = createMemo(() => can(venue.role(), 'room.view') && venue.role() !== 'support'
    && (venue.deviceDoc()?.controllerOutputView === 'output-view/1' || onLink()));
  const available = createMemo(() => visible()
    && Boolean(venue.selectedVenue()?.venueId && venue.selectedRoom()?.roomId && venue.user())
    && (onLink() ? venue.roomLinkState() === 'open' : venue.cloudReachable() === true));
  const active = createMemo(() => phase() !== 'idle');
  const remaining = createMemo<OutputViewRemaining | undefined>(() => {
    if (phase() !== 'active') return undefined;
    if (sessionExtended()) return { kind: 'indefinite' };
    const deadline = deadlineMs();
    return deadline === undefined ? undefined : { kind: 'countdown', deadlineMs: deadline };
  });
  let peer: OutputViewPeer | undefined;
  let sessionId = '';
  let generation = 0;
  let map: WireMap | null = null;
  /**
   * Wire-map index → element address, computed once per map, layout, rig or placement change (never per
   * frame). Undefined while there is no map or the map and the drawing disagree; nothing is drawn then.
   */
  let wireKeys: readonly string[] | undefined;
  let queuedFrames: WireFrame[] = [];
  /**
   * Each element's last level on the wire under the current map and drawing. A frame copies only the
   * datagrams sent in its tick, and destinations are sent on their own clocks (each OSC box at its refresh
   * rate), so a frame may carry no datagram for an element; the receiver then still shows its last value,
   * and so does the view. Cleared with the picture: on a new map, an unpaired drawing, or the end.
   */
  let wireLevels = new Map<string, number>();
  let lastFrameSeq = -1n;
  let clockOffsetUs = 0n;
  let wireSilenceTimer: ReturnType<typeof setTimeout> | undefined;
  let lastThinnedFrames: string | undefined;
  let raf = 0;
  let endedSession = '';
  let stopping = false;
  let sessionScope: { venueId: string; roomId: string } | undefined;
  let takeoverNameUnsubscribe: Unsubscribe | undefined;
  /** The running stream's phase timings: `performance.now()` at the grant request, then each mark's offset. */
  let phaseOrigin = 0;
  let phaseSessionId = '';
  let phaseMarks: Partial<Record<PhaseMark, number>> = {};
  let answerVia: OutputViewAnswerVia | undefined;
  let phasesLogged = false;
  /** The view on the link: the request's `performance.now()` and each S29 mark's offset from it. */
  let linkOrigin = 0;
  let linkMarks: { liveViewAckMs: number | null; firstMapMs: number | null; liveViewFirstFrameMs: number | null } | undefined;
  let hiddenTimer: ReturnType<typeof setTimeout> | undefined;

  function markLink(mark: 'liveViewAckMs' | 'firstMapMs' | 'liveViewFirstFrameMs'): void {
    if (linkMarks && linkMarks[mark] === null) linkMarks[mark] = Math.round(performance.now() - linkOrigin);
  }

  /** S29: one line per view on the link, when its first frame paints or when it ends first. */
  function logLinkPhases(): void {
    if (!linkMarks) return;
    console.info(`${ROOM_LINK_LIVE_VIEW_PREFIX}${JSON.stringify(linkMarks)}`);
    linkMarks = undefined;
  }

  /** A view on the link ends after the tab is hidden for 30 s, as the session's peer does (B74). */
  const linkVisibilityChanged = (): void => {
    if (hiddenTimer) clearTimeout(hiddenTimer);
    hiddenTimer = undefined;
    if (document.visibilityState === 'hidden') {
      hiddenTimer = setTimeout(() => { if (document.visibilityState === 'hidden') stop(); }, OUTPUT_VIEW_HIDDEN_GRACE_MS);
    }
  };

  function releaseLinkVisibility(): void {
    document.removeEventListener('visibilitychange', linkVisibilityChanged);
    if (hiddenTimer) clearTimeout(hiddenTimer);
    hiddenTimer = undefined;
  }

  function markPhase(mark: PhaseMark): void {
    if (phaseSessionId && phaseMarks[mark] === undefined) phaseMarks[mark] = Math.round(performance.now() - phaseOrigin);
  }

  /**
   * One line per stream: when its first frame paints, or at its end if it ends first. A mark the stream
   * never reached is null; `listenerLagMs` is the session listener's worst lag (null when unknown).
   */
  function logPhases(end: OutputViewEndReason | null, from: OutputViewPeer | undefined): void {
    if (!phaseSessionId || phasesLogged) return;
    phasesLogged = true;
    const marks = Object.fromEntries(PHASE_MARKS.map((mark) => [mark, phaseMarks[mark] ?? null]));
    console.info(`${OUTPUT_VIEW_PHASES_PREFIX}${JSON.stringify({ sessionId: phaseSessionId, end, ...marks,
      answerVia: answerVia ?? null, listenerLagMs: from?.listenerLagMs() ?? null })}`);
  }

  const clearTakeoverName = (): void => {
    takeoverNameUnsubscribe?.();
    takeoverNameUnsubscribe = undefined;
  };

  const endCloud = async (id: string, scope = sessionScope): Promise<void> => {
    if (!id || endedSession === id) return;
    if (!scope) return;
    endedSession = id;
    try {
      await venue.cloudCall('endOutputViewSession', { ...scope, sessionId: id });
    } catch { /* The peer still sends its own end if the broker cannot be reached. */ }
  };

  function clearDrawing(): void {
    if (raf) cancelAnimationFrame(raf);
    if (wireSilenceTimer) clearTimeout(wireSilenceTimer);
    wireSilenceTimer = undefined;
    lastThinnedFrames = undefined;
    setReportedWire(NOT_CONNECTED);
    setMapState(null);
    raf = 0;
    queuedFrames = [];
    lastFrameSeq = -1n;
    map = null;
    wireKeys = undefined;
    wireLevels = new Map();
    setLevels(EMPTY_LEVELS);
    setUnknownKeys(new Set<string>());
  }

  function finish(endReason: OutputViewEndReason, byDisplayName?: string, endedPath?: string): void {
    const id = sessionId;
    logPhases(endReason, peer);
    logLinkPhases();
    releaseLinkVisibility();
    clearTakeoverName();
    peer = undefined;
    sessionId = '';
    stopping = false;
    generation += 1;
    const settledGeneration = generation;
    clearDrawing();
    setPhase('idle');
    setHolder(undefined);
    setDeadlineMs(undefined);
    setReason(outputViewReason(endReason, byDisplayName));
    if (endedPath && ((endReason === 'taken_over' && !byDisplayName)
      || endReason === 'transport_closed' || endReason === 'connect_timeout')) {
      takeoverNameUnsubscribe = onSnapshot(doc(firestore, endedPath), (snapshot) => {
        const ended = snapshot.data();
        if (generation !== settledGeneration || ended?.state !== 'ENDED') return;
        if (ended.endReason === 'taken-over') {
          if (typeof ended.endedByDisplayName !== 'string' || !ended.endedByDisplayName) return;
          setReason(outputViewReason('taken_over', ended.endedByDisplayName));
        } else if (ended.endReason === 'ended-by-requester'
          && endReason !== 'connect_timeout') {
          // A failed peer ends its grant for cleanup; that end was not a user Stop.
          setReason(outputViewReason('client_end'));
        } else if (CONTROLLER_END_REASONS.has(ended.endReason as OutputViewEndReason)) {
          setReason(outputViewReason(ended.endReason as OutputViewEndReason));
        }
        clearTakeoverName();
      }, () => {
        if (generation === settledGeneration && endReason === 'taken_over') setReason(outputViewReason('transport_closed'));
        clearTakeoverName();
      });
    }
    if (endReason === 'client_end' || endReason === 'connect_timeout'
        || endReason === 'transport_closed' || endReason === 'transport_policy') void endCloud(id);
    sessionScope = undefined;
  }

  /**
   * One animation frame on the display's own clock: show the newest queued frame whose playout time has
   * passed, dropping the older ones. There is no paint interval of its own; a fixed interval beats against
   * the frame rate and the vsync grid and drops frames unevenly (judder).
   */
  function paint(): void {
    raf = 0;
    const keys = wireKeys;
    if (!active() || !queuedFrames.length || !map || !keys) return;
    const playedOutUs = BigInt(Date.now()) * 1000n + clockOffsetUs - BigInt(PLAYOUT_MS) * 1000n;
    let readyIndex = -1;
    for (let index = 0; index < queuedFrames.length; index += 1) {
      if (queuedFrames[index].sentAtUs <= playedOutUs) readyIndex = index;
    }
    if (readyIndex < 0) {
      raf = requestAnimationFrame(paint);
      return;
    }
    const frame = queuedFrames[readyIndex];
    queuedFrames.splice(0, readyIndex + 1);
    lastFrameSeq = frame.frameSeq;
    const frameLevels = elementLevels(map, frame);
    for (let index = 0; index < keys.length; index += 1) {
      const fraction = levelFraction(frameLevels[index]);
      if (fraction !== undefined) wireLevels.set(keys[index], fraction);
    }
    setLevels(new Map(wireLevels));
    if (phaseMarks.firstFrame === undefined) {
      markPhase('firstFrame');
      logPhases(null, peer);
    }
    if (linkMarks?.liveViewFirstFrameMs === null) {
      markLink('liveViewFirstFrameMs');
      logLinkPhases();
    }
    if (queuedFrames.length) raf = requestAnimationFrame(paint);
  }

  /** Hearing the Pi (a status, a wire change or an accepted frame) restarts the 3 s silence clock. */
  function heardFromController(): void {
    if (wireSilenceTimer) clearTimeout(wireSilenceTimer);
    wireSilenceTimer = setTimeout(() => { wireSilenceTimer = undefined; setReportedWire(NOT_CONNECTED); }, WIRE_SILENCE_MS);
  }

  /**
   * A complete frame from the frame channel. A fresh, in-sequence frame proves the view still hears the
   * Pi, whether or not this drawing can use it; it keeps the last reported wire state from expiring.
   */
  function acceptFrame(frame: WireFrame, offsetUs: bigint): void {
    if (!active() || frame.frameSeq <= lastFrameSeq) return;
    const ageUs = BigInt(Date.now()) * 1000n + offsetUs - frame.sentAtUs;
    if (ageUs > 1_000_000n) return;
    heardFromController();
    if (!map || !wireKeys) return;
    clockOffsetUs = offsetUs;
    if (queuedFrames.some((pending) => pending.frameSeq === frame.frameSeq)) return;
    const at = queuedFrames.findIndex((pending) => pending.frameSeq > frame.frameSeq);
    if (at < 0) queuedFrames.push(frame);
    else queuedFrames.splice(at, 0, frame);
    if (queuedFrames.length > MAX_PLAYOUT_FRAMES) queuedFrames.splice(0, queuedFrames.length - MAX_PLAYOUT_FRAMES);
    if (!raf) raf = requestAnimationFrame(paint);
  }

  /**
   * Pair the map with the drawing: runs on a new map and on every layout, rig or placement emission, and
   * is the only place the wire order is computed. While the pairing holds, the current picture and the
   * queued frames stay (frames index the map, not the layout), so a re-emitted layout never blanks the
   * view. Without a map, or when the map and the drawing disagree, nothing can be drawn: the view blanks.
   */
  function reconcileMapLayout(layout = venue.roomLayout()): void {
    const currentMap = map;
    const ordered = currentMap && layout ? wireOrderedSchematicElements(layout, venue.rig(), venue.placementTargets()) : [];
    const paired = Boolean(currentMap && layout && currentMap.layoutDigest === layout.layoutDigest
      && currentMap.entries.length === ordered.length && ordered.every(({ deviceId }) => deviceId));
    wireKeys = paired ? ordered.map(({ element }) => elementAddress(element.deviceId, element.elementIndex)) : undefined;
    setUnknownKeys(new Set(wireKeys?.filter((_, index) => currentMap!.entries[index].proto === 0) ?? []));
    if (wireKeys) return;
    queuedFrames = [];
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    wireLevels = new Map();
    setLevels(EMPTY_LEVELS);
  }

  /**
   * One wire report. A `wire` change message carries no reason, so a change of state drops the previous
   * reason until the next status (within a second) supplies it; the Pi sends both in the same tick.
   */
  function acceptWire(state: OutputViewWireState, reasonFromStatus: OutputViewWireReason | undefined,
    fromStatus: boolean, thinnedFramesFromStatus?: string): void {
    const previous = reportedWire();
    const reason = fromStatus ? reasonFromStatus
      : previous.state === state ? (previous as { reason?: OutputViewWireReason }).reason : undefined;
    if (thinnedFramesFromStatus && (!lastThinnedFrames
      || BigInt(thinnedFramesFromStatus) >= BigInt(lastThinnedFrames))) lastThinnedFrames = thinnedFramesFromStatus;
    const thinnedFrames = lastThinnedFrames;
    setReportedWire(reason ? { state, reason, thinnedFrames } : { state, thinnedFrames });
    heardFromController();
  }

  /** A new map (or none, while one is in transfer or failed): frames from before it no longer apply. */
  function acceptMap(next: WireMap | null): void {
    if (next) markPhase('firstMap');
    if (next) markLink('firstMapMs');
    map = next;
    queuedFrames = [];
    wireLevels = new Map();
    lastFrameSeq = -1n;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    reconcileMapLayout();
  }

  /**
   * The view's handlers on the link for one request (`turn`): the controller's ack, busy answer and end, and
   * the picture the link carries. A handler from an earlier request does nothing.
   */
  function linkHandlers(turn: number): RoomLinkLiveViewHandlers {
    return {
      onAck: (ack) => {
        if (turn !== generation) return;
        markLink('liveViewAckMs');
        // B113 on the link: the controller's own clock says how long is left; this console counts it down
        // from the moment the ack arrived, never past the ceiling the controller reports.
        const now = Date.now();
        batch(() => {
          setSessionDuration(ack.duration);
          setPendingExtended(undefined);
          setDeadlineMs(ack.endsInMs === null ? undefined : now + Math.min(ack.endsInMs, ack.ceilingInMs));
          setHolder(undefined);
          setPhase('active');
        });
      },
      onBusy: (busy) => {
        if (turn !== generation) return;
        generation += 1;
        logLinkPhases();
        releaseLinkVisibility();
        batch(() => {
          setPhase('idle');
          setHolder(holderFromLink(busy));
        });
        sessionScope = undefined;
      },
      onEnd: (reason, byDisplayName) => { if (turn === generation) finish(reason, byDisplayName); },
      onMap: (next) => { if (turn === generation) acceptMap(next); },
      onMapState: (state) => { if (turn === generation) setMapState(state); },
      onWire: (report) => { if (turn === generation) acceptWire(report.state, report.reason, report.from === 'status', report.thinnedFrames); },
      onFrame: (frame, offset) => { if (turn === generation) acceptFrame(frame, offset); },
    };
  }

  /** Start (or take over) the view on the room's open link: one message, answered by the controller (D14). */
  function startOnLink(action: 'start' | 'takeOver'): void {
    const link = venue.roomLink();
    if (!link) return;
    const duration: RoomLinkLiveViewDuration = extended() ? 'extended' : 'default';
    sessionScope = { venueId: venue.selectedVenue()!.venueId, roomId: venue.selectedRoom()!.roomId };
    clearTakeoverName();
    batch(() => {
      setExtended(false);
      setReason('');
      setHolder(undefined);
      setLevels(EMPTY_LEVELS);
      setReportedWire(NOT_CONNECTED);
      setMapState(null);
      setSessionDuration(duration);
      setDeadlineMs(undefined);
      setPhase('connecting');
    });
    lastThinnedFrames = undefined;
    const turn = ++generation;
    phaseSessionId = '';
    linkOrigin = performance.now();
    linkMarks = { liveViewAckMs: null, firstMapMs: null, liveViewFirstFrameMs: null };
    const sent = action === 'start' ? link.liveViewStart(duration, linkHandlers(turn)) : link.liveViewTakeOver(duration, linkHandlers(turn));
    if (!sent) { finish('transport_closed'); return; }
    document.addEventListener('visibilitychange', linkVisibilityChanged);
    linkVisibilityChanged();
  }

  async function start(): Promise<void> {
    if (!available() || active()) return;
    if (onLink()) { startOnLink('start'); return; }
    // Only the live runner sets this option. The broker limits it to its claimed dev gate account.
    const gate = (globalThis as typeof globalThis & { __NUANCE_OUTPUT_VIEW_GATE_REQUEST__?: boolean })
      .__NUANCE_OUTPUT_VIEW_GATE_REQUEST__ === true;
    const duration: OutputViewDuration = gate ? 'livegate' : extended() ? 'extended' : 'default';
    const scope = { venueId: venue.selectedVenue()!.venueId, roomId: venue.selectedRoom()!.roomId };
    sessionScope = scope;
    clearTakeoverName();
    setExtended(false);
    setReason('');
    setHolder(undefined);
    setLevels(EMPTY_LEVELS);
    setReportedWire(NOT_CONNECTED);
    lastThinnedFrames = undefined;
    setMapState(null);
    setSessionDuration(duration);
    setDeadlineMs(undefined);
    setPhase('connecting');
    const turn = ++generation;
    phaseSessionId = '';
    phaseMarks = {};
    answerVia = undefined;
    phasesLogged = false;
    try {
      const key = await createOutputViewKey();
      if (turn !== generation) return;
      phaseOrigin = performance.now();
      const response = await venue.cloudCall<OutputViewGrant | { busy: true; holder: OutputViewHolder }>('requestOutputViewSession', {
        ...scope,
        client: gate ? 'wireview' : 'console', duration, clientEphemeralPublicKey: key.publicKey,
      });
      if (turn !== generation) {
        if (!response.busy) void endCloud(response.sessionId, scope);
        return;
      }
      if (response.busy) {
        setPhase('idle');
        setHolder(holderFromSession(response.holder));
        sessionScope = undefined;
        return;
      }
      sessionId = response.sessionId;
      sessionScope = scope;
      endedSession = '';
      phaseSessionId = response.sessionId;
      markPhase('grantReceived');
      peer = new OutputViewPeer(firestore, restFirestore, response, key, {
        onReady: (ackedDuration, offsetUs, hardExpiresAt) => {
          if (turn !== generation) return;
          markPhase('helloAck');
          clockOffsetUs = offsetUs;
          setSessionDuration(ackedDuration);
          setDeadlineMs(outputViewDeadline(ackedDuration, Date.now(), hardExpiresAt, offsetUs));
          setPhase('active');
        },
        onPhase: (mark: OutputViewPeerPhase, via) => {
          if (turn !== generation) return;
          if (via) answerVia = via;
          markPhase(mark);
        },
        onWire: (report) => { if (turn === generation) acceptWire(report.state, report.reason,
          report.from === 'status', report.thinnedFrames); },
        onFrame: (frame, offset) => { if (turn === generation) acceptFrame(frame, offset); },
        onMap: (next) => { if (turn === generation) acceptMap(next); },
        onMapState: (state) => { if (turn === generation) setMapState(state); },
        onEnd: (why, byDisplayName) => {
          if (turn === generation) finish(why, byDisplayName, response.signalingPath);
        },
        renew: async () => {
          const grant = await venue.cloudCall<{ ticket: string; iceServers: OutputViewGrant['iceServers'] }>('renewOutputViewSession', {
            ...scope,
            sessionId: response.sessionId,
          });
          return grant;
        },
      });
      await peer.start();
    } catch (error) {
      if (turn !== generation) return;
      const hadGrant = Boolean(sessionId);
      if (sessionId) void endCloud(sessionId);
      peer?.stop();
      peer = undefined;
      sessionId = '';
      sessionScope = undefined;
      clearDrawing();
      setDeadlineMs(undefined);
      setPhase('idle');
      setReason(hadGrant ? outputViewReason('transport_closed')
        : isNotMemberRefusal(error) ? 'You are not a member of this venue.' : plainError(error).message);
    }
  }

  function stop(): void {
    if (!active() || stopping) return;
    if (onLink() && !peer) {
      venue.roomLink()?.liveViewStop();
      finish('client_end');
      return;
    }
    const current = peer;
    if (!current) { finish('client_end'); return; }
    stopping = true;
    // The broker commits ended-by-requester before closing the data channels. A close first lets
    // the Pi report transport_closed and permanently wins the cloud session's end-reason race.
    void endCloud(sessionId).then(() => {
      if (peer === current) current.stop();
    });
  }

  async function setSessionExtended(value: boolean): Promise<void> {
    if (onLink() && !peer) {
      const duration: RoomLinkLiveViewDuration = value ? 'extended' : 'default';
      if (!sessionSwitchable() || sessionDuration() === duration) return;
      // B114 on the link: the indicator follows the click at once; the controller's liveViewAck confirms it and
      // restarts a default view's two minutes on its own clock (B113).
      const runningDeadline = deadlineMs();
      batch(() => {
        setPendingExtended(value);
        if (!value) setDeadlineMs(Date.now() + 120_000);
      });
      if (!venue.roomLink()?.liveViewSetDuration(duration)) {
        batch(() => {
          setPendingExtended(undefined);
          setDeadlineMs(runningDeadline);
        });
      }
      return;
    }
    const current = peer;
    const id = sessionId;
    const scope = sessionScope;
    const duration = value ? 'extended' : 'default';
    if (!current || !id || !scope || !sessionSwitchable() || sessionDuration() === duration) return;
    // The indicator follows the viewer's own request at once (B114): indefinite on the click, or two minutes
    // while a switch back is in flight. A refusal restores the deadline the running stream still has.
    const runningDeadline = deadlineMs();
    batch(() => {
      setPendingExtended(value);
      if (!value) setDeadlineMs(Date.now() + 120_000);
    });
    try {
      const switched = await venue.cloudCall<{ ticket: string; hardExpiresAt: number; iceServers: OutputViewGrant['iceServers'] }>('setOutputViewSessionDuration', {
        ...scope, sessionId: id, duration,
      });
      if (peer !== current) return;
      current.changeDuration(duration, switched);
      // The controller restarts a default stream's 120 s when this grant reaches it (B113).
      setDeadlineMs(outputViewDeadline(duration, Date.now(), switched.hardExpiresAt, clockOffsetUs));
      setSessionDuration(duration);
      setReason('');
    } catch (error) {
      if (peer === current) {
        batch(() => {
          setPendingExtended(undefined);
          setDeadlineMs(runningDeadline);
        });
        setReason(isNotMemberRefusal(error) ? 'You are not a member of this venue.' : plainError(error).message);
      }
    } finally {
      setPendingExtended(undefined);
    }
  }

  async function takeOver(): Promise<void> {
    const busy = holder();
    if (!busy || !can(venue.role(), 'stream.disconnect')) return;
    if (onLink()) {
      // B77 on the link: one message; the controller ends the holder's view `taken_over` and starts this one.
      if (available() && !active()) startOnLink('takeOver');
      return;
    }
    try {
      await venue.cloudCall('endOutputViewSession', {
        venueId: venue.selectedVenue()?.venueId,
        roomId: venue.selectedRoom()?.roomId,
        sessionId: busy.sessionId,
      });
    } catch (error) {
      setReason(isNotMemberRefusal(error) ? 'You are not a member of this venue.' : plainError(error).message);
      return;
    }
    setHolder(undefined);
    await start();
  }

  createEffect(() => {
    const layout = venue.roomLayout();
    venue.rig();
    venue.placementTargets();
    if (map && active()) reconcileMapLayout(layout);
  });
  createEffect(() => {
    if (active() && (!available() || !venue.user())) stop();
  });
  createEffect(() => {
    if (active() && sessionScope && (venue.selectedVenue()?.venueId !== sessionScope.venueId
      || venue.selectedRoom()?.roomId !== sessionScope.roomId)) stop();
  });
  // Runs only when the provider goes away (app teardown); sign-out is the `user()` effect above.
  onCleanup(() => { clearTakeoverName(); if (active()) stop(); });

  return {
    phase, active, visible, available, extended, setExtended, levels, unknownKeys, holder, remaining, reason,
    wire, sessionExtended, sessionSwitchable, setSessionExtended, start, stop, takeOver,
  };
}
